import { useState, useCallback } from "react";

const DIRECTION_MAP = {
  down: 0,
  up: 180,
  left: 90,
  right: 270,
};

const KEY_DIRECTION_MAP = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  up: "up",
  down: "down",
  left: "left",
  right: "right",
};

/**
 * Arrow Component
 *
 * @param {number|string} width - Width of the arrow SVG (default: 12)
 * @param {number|string} height - Height of the arrow SVG (default: 7)
 * @param {string} color - Color of the arrow fill (overrides fillColor)
 * @param {string} fillColor - Fill color of the arrow (default: "#4A4A68")
 * @param {string|number} direction - Direction ("up", "down", "left", "right") or rotation degrees (0, 90, 180, 270)
 * @param {string} defaultDirection - Initial direction if direction is uncontrolled (default: "down")
 * @param {boolean} enableKeyControl - Enable rotation on keyboard arrow keys when focused (default: true)
 * @param {function} onDirectionChange - Callback when direction changes via keyboard key
 */
export const Arrow = ({
  width = 12,
  height = 7,
  color,
  fillColor = "#4A4A68",
  direction,
  defaultDirection = "down",
  enableKeyControl = true,
  onDirectionChange,
  onKeyDown,
  style,
  tabIndex = 0,
  ...props
}) => {
  const [internalDirection, setInternalDirection] = useState(defaultDirection);

  const activeDirection = direction !== undefined ? direction : internalDirection;

  const getRotationDegrees = dir => {
    if (typeof dir === "number") return dir;
    if (typeof dir === "string" && DIRECTION_MAP[dir.toLowerCase()] !== undefined) {
      return DIRECTION_MAP[dir.toLowerCase()];
    }
    return 0;
  };

  const handleKeyDown = useCallback(
    e => {
      if (enableKeyControl) {
        const newDir = KEY_DIRECTION_MAP[e.key] || KEY_DIRECTION_MAP[e.key?.toLowerCase()];
        if (newDir) {
          e.preventDefault();
          setInternalDirection(newDir);
          if (onDirectionChange) {
            onDirectionChange(newDir, e);
          }
        }
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [enableKeyControl, onDirectionChange, onKeyDown],
  );

  const rotation = getRotationDegrees(activeDirection);
  const activeColor = color || fillColor;

  return (
    <span
      role="img"
      aria-label="arrow"
      tabIndex={enableKeyControl ? tabIndex : undefined}
      onKeyDown={handleKeyDown}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        outline: "none",
        cursor: enableKeyControl ? "pointer" : "default",
        ...style,
      }}
      {...props}
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: "transform 0.2s ease-in-out",
          transformOrigin: "center center",
        }}
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 0.904949C0.00355721 0.957892 0.00312805 0.971368 0.0119572 1.02379C0.0318871 1.14166 0.0756464 1.25503 0.139935 1.35505C0.172422 1.40563 0.210224 1.44978 0.250554 1.49388L5.39364 6.74439C5.43166 6.78062 5.44031 6.79072 5.48184 6.82288C5.52848 6.85898 5.57875 6.89018 5.63164 6.91586C5.86269 7.02805 6.13686 7.02805 6.36791 6.91586C6.4208 6.89018 6.47107 6.85898 6.5177 6.82288C6.55919 6.79072 6.56789 6.78062 6.60587 6.74439L11.749 1.49388C11.7844 1.45507 11.7944 1.44619 11.8258 1.40383C11.9085 1.29261 11.9643 1.16135 11.9876 1.02379C12.0041 0.925557 12.0041 0.824616 11.9876 0.726388C11.9743 0.647849 11.9505 0.571236 11.917 0.49926C11.7896 0.225884 11.5255 0.035552 11.2305 0.0045743C11.114 -0.00763321 10.9952 0.00483704 10.8835 0.0410218C10.7905 0.0711684 10.7027 0.117591 10.6249 0.177797C10.5834 0.209957 10.5747 0.220064 10.5367 0.256292L5.99975 4.88803L1.46278 0.256292L1.42001 0.215514C1.37972 0.182042 1.37033 0.172503 1.32679 0.143363C1.24518 0.0887141 1.15445 0.04846 1.05965 0.0248766C0.983787 0.00601864 0.90544 -0.00220728 0.827437 0.000505447C0.531067 0.0108314 0.25484 0.182349 0.109505 0.446187C0.0616741 0.533039 0.0285435 0.628203 0.0119572 0.726388C0.00312805 0.778805 0.00355721 0.792282 0 0.845225C0 0.865133 0 0.885041 0 0.904949Z"
          fill={activeColor}
        />
      </svg>
    </span>
  );
};

export default Arrow;
